import React from 'react'
import { Input } from '@/components/ui'
import { normalizeHex } from '@/features/organization/utils/organizationFormUtils'
import type { OrganizationFormValues } from '@/features/organization/schemas/organizationForm.schema'
import type { FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { cn } from '@/lib/cn'
import { FieldGroup, SettingsPane } from '../organization-ui'
import { LogoField } from './LogoField'

const DEFAULT_PRIMARY_COLOR = '#004A6B'
const hexColorRegex = /^#([0-9A-Fa-f]{3}){1,2}$/

const PRESET_COLORS: readonly { hex: string; name: string }[] = [
  { hex: '#004A6B', name: 'Harbor' },
  { hex: '#1E40AF', name: 'Cobalt' },
  { hex: '#0F766E', name: 'Pine' },
  { hex: '#047857', name: 'Fern' },
  { hex: '#B45309', name: 'Amber' },
  { hex: '#BE123C', name: 'Crimson' },
  { hex: '#6D28D9', name: 'Violet' },
  { hex: '#374151', name: 'Graphite' },
] as const

/** Relative luminance per WCAG, used to keep preview text legible on any accent. */
function readableInkFor(hex: string): string {
  const full =
    hex.length === 4
      ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex
  const channel = (offset: number): number => {
    const value = parseInt(full.slice(offset, offset + 2), 16) / 255
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  }
  const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5)
  return luminance > 0.42 ? '#111827' : '#ffffff'
}

export interface BrandingPaneProps {
  primaryColorValue: string | undefined
  register: UseFormRegister<OrganizationFormValues>
  setValue: UseFormSetValue<OrganizationFormValues>
  errors: FieldErrors<OrganizationFormValues>
  disabled?: boolean
}

export const BrandingPane: React.FC<BrandingPaneProps> = ({
  primaryColorValue,
  register,
  setValue,
  errors,
  disabled = false,
}) => {
  const isValid = Boolean(primaryColorValue && hexColorRegex.test(primaryColorValue))
  const accent = isValid ? (primaryColorValue as string) : DEFAULT_PRIMARY_COLOR
  const ink = readableInkFor(accent)

  const applyColor = (next: string): void =>
    setValue('primaryColor', next, { shouldDirty: true, shouldValidate: true })

  return (
    <SettingsPane
      title="Brand styling"
      description="The logo, accent color, and footer people see across the workspace."
    >
      <FieldGroup label="Logo">
        <LogoField disabled={disabled} />
      </FieldGroup>

      <FieldGroup label="Primary color">
        <div className="flex flex-wrap items-end gap-3">
          <input
            type="color"
            aria-label="Pick primary color"
            disabled={disabled}
            className="h-11 w-14 shrink-0 cursor-pointer rounded-xl border border-gray-200 bg-white p-1 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800"
            value={accent}
            onChange={(event) => applyColor(event.target.value)}
          />
          <div className="min-w-[12rem] max-w-xs flex-1">
            <Input
              label="Hex value"
              type="text"
              placeholder={DEFAULT_PRIMARY_COLOR}
              error={errors.primaryColor?.message}
              disabled={disabled}
              className="font-mono uppercase tracking-[0.04em]"
              {...register('primaryColor', {
                onChange: (event) => {
                  const next = normalizeHex(event.target.value)
                  if (next !== event.target.value) {
                    setValue('primaryColor', next, { shouldDirty: true })
                  }
                },
              })}
            />
          </div>
        </div>

        {/*
          Swatches only — deliberately not mock buttons. An earlier version drew a
          fake "Save changes" button here, which read as the real control.
        */}
        <div
          className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-gray-200 bg-gray-50/70 px-4 py-3.5 dark:border-gray-700 dark:bg-gray-900/30"
          role="group"
          aria-label="Accent color preview"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
            Preview
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <span className="flex items-center gap-2">
              <span
                className="inline-flex h-6 items-center rounded-full px-2 text-[11px] font-semibold"
                style={{ backgroundColor: accent, color: ink }}
              >
                Badge
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">on accent</span>
            </span>
            <span className="flex items-center gap-2">
              <span
                className="h-6 w-10 rounded-full shadow-[inset_0_0_0_1px_oklch(0_0_0/0.08)]"
                style={{ backgroundColor: accent }}
                aria-hidden
              />
              <span className="text-xs text-gray-500 dark:text-gray-400">fill</span>
            </span>
            <span className="flex items-center gap-2">
              <span className="text-sm font-medium underline underline-offset-4" style={{ color: accent }}>
                Text link
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">on white</span>
            </span>
          </div>
        </div>
      </FieldGroup>

      <FieldGroup label="Presets">
        <div className="flex flex-wrap gap-2">
          {PRESET_COLORS.map((preset) => {
            const isSelected = accent.toLowerCase() === preset.hex.toLowerCase()
            return (
              <button
                key={preset.hex}
                type="button"
                disabled={disabled}
                onClick={() => applyColor(preset.hex)}
                aria-pressed={isSelected}
                title={`${preset.name} · ${preset.hex}`}
                className={cn(
                  'group flex items-center gap-2 rounded-lg border py-1.5 pl-1.5 pr-3 text-xs font-medium transition-[border-color,background-color,scale] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:scale-[0.96] disabled:cursor-not-allowed disabled:opacity-50',
                  isSelected
                    ? 'border-gray-400 bg-gray-100 text-gray-900 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-100'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                )}
              >
                <span
                  className="h-6 w-6 rounded-md shadow-[inset_0_0_0_1px_oklch(0_0_0/0.1)]"
                  style={{ backgroundColor: preset.hex }}
                  aria-hidden
                />
                {preset.name}
              </button>
            )
          })}
        </div>
        <p className="mt-3 max-w-2xl text-xs leading-5 text-gray-500 dark:text-gray-400">
          Pick a color that stays readable when text sits on top of it. The preview above updates
          instantly; the workspace applies it after you save.
        </p>
      </FieldGroup>

      <FieldGroup label="Footer and legal links">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Input
              label="Footer text"
              type="text"
              placeholder="Acme Ltd. Registered in England."
              maxLength={200}
              error={errors.footerText?.message}
              disabled={disabled}
              {...register('footerText')}
            />
          </div>
          <Input
            label="Terms link"
            type="url"
            placeholder="https://example.com/terms"
            error={errors.termsUrl?.message}
            disabled={disabled}
            {...register('termsUrl')}
          />
          <Input
            label="Privacy link"
            type="url"
            placeholder="https://example.com/privacy"
            error={errors.privacyUrl?.message}
            disabled={disabled}
            {...register('privacyUrl')}
          />
        </div>
        <p className="mt-3 max-w-2xl text-xs leading-5 text-gray-500 dark:text-gray-400">
          Shown at the bottom of the workspace. The links also appear on your sign-in page.
        </p>
      </FieldGroup>
    </SettingsPane>
  )
}

BrandingPane.displayName = 'BrandingPane'
